import React, { useState, useEffect, useRef, useCallback } from 'react';
import { confirmDialog } from '../ConfirmDialog';
import {
  Lock,
  Unlock,
  Plus,
  Settings,
  X,
  Check,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  Maximize2,
  Filter,
  Columns3,
  Loader2,
  GripVertical
} from 'lucide-react';
import ExportMenu from '../ExportMenu';
import './EditableDataTable.css';

const EditableDataTable = ({
  // Data props
  data = [],
  columns = [],
  idField = 'id',
  
  // Feature flags
  enableEdit = true,
  enableSort = true,
  enableFilter = true,
  enableColumnSelector = true,
  enableDelete = true,
  enableAdd = true,
  enableSearch = true,
  enableLock = true,
  enableResize = true,
  enableFullscreen = true,
  enableExport = true,
  
  // Callbacks - Support multiple naming conventions
  onUpdate = async () => {},
  onCellUpdate,  // Alternative name for (rowId, field, value, rowData)
  onCellChange,  // Alternative name for (rowId, field, value, rowData)
  onChange,      // Alternative name
  onDelete = async () => {},
  onAdd = async () => {},
  
  // Customization
  title = '',
  emptyMessage = 'No data available',
  searchPlaceholder = 'Search...',
  addButtonText = 'Add New',
  defaultVisibleColumns = null,
  lockedColumns = [],
  nonEditableColumns = [],
  
  // Styling
  className = '',
  compactMode = false,

  // Optional function to add custom class names to rows: (row, index) => string
  rowClassName = null,

  // Optional secondary sort fields to preserve grouping when primary sort has equal values
  // Format: [{ key: 'field_name', direction: 'asc' | 'desc' }, ...]
  secondarySortFields = null,
}) => {
  // Determine which update callback to use
  const updateCallback = onCellUpdate || onCellChange || onChange || onUpdate;
  
  // Determine the callback signature format
  // If onCellUpdate or onCellChange is provided, use extended signature
  const isExtendedCallback = !!(onCellUpdate || onCellChange);
  
  // State management
  const [isLocked, setIsLocked] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeFilters, setActiveFilters] = useState({});
  const [showFilterDropdown, setShowFilterDropdown] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem(`table-columns-${title}`);
    return saved ? JSON.parse(saved) : (defaultVisibleColumns || columns.map(c => c.key));
  });
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);
  const [savedCell, setSavedCell] = useState(null);
  // Inline error notice (alerts are banned) — string message or null,
  // rendered as a dismissible banner above the table.
  const [actionError, setActionError] = useState(null);
  // Optimistic cell values keyed `${rowId}::${columnKey}` — keeps an edited
  // value on screen after save instead of snapping back to the stale prop
  // until the parent refetches. Cleared when fresh data arrives.
  const [cellOverrides, setCellOverrides] = useState({});
  const [showAddRow, setShowAddRow] = useState(false);
  const [newRowData, setNewRowData] = useState({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem(`table-column-widths-${title}`);
    return saved ? JSON.parse(saved) : {};
  });
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  
  // Refs
  const filterDropdownRefs = useRef({});
  const tableRef = useRef(null);
  const resizeRefs = useRef({});

  // Fresh data from the parent supersedes any optimistic cell values
  useEffect(() => {
    setCellOverrides({});
  }, [data]);

  // Save column preferences
  useEffect(() => {
    if (title) {
      localStorage.setItem(`table-columns-${title}`, JSON.stringify(visibleColumns));
      localStorage.setItem(`table-column-widths-${title}`, JSON.stringify(columnWidths));
    }
  }, [visibleColumns, columnWidths, title]);

  // Handle clicks outside filter dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilterDropdown && filterDropdownRefs.current[showFilterDropdown]) {
        if (!filterDropdownRefs.current[showFilterDropdown].contains(event.target)) {
          setShowFilterDropdown(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterDropdown]);

  // Handle column resize
  const handleMouseDown = (e, columnKey) => {
    if (!enableResize) return;
    
    setIsResizing(true);
    setResizingColumn(columnKey);
    setStartX(e.clientX);
    setStartWidth(columnWidths[columnKey] || 150);
    
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!isResizing || !resizingColumn) return;
    
    const diff = e.clientX - startX;
    const newWidth = Math.max(50, startWidth + diff);
    
    setColumnWidths(prev => ({
      ...prev,
      [resizingColumn]: newWidth
    }));
  }, [isResizing, resizingColumn, startX, startWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    setResizingColumn(null);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (!isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Get unique values for filtering
  const getUniqueValues = (columnKey) => {
    const values = new Set();
    data.forEach(row => {
      const value = row[columnKey];
      if (value !== null && value !== undefined && value !== '') {
        const column = columns.find(c => c.key === columnKey);
        const formattedValue = column?.format ? column.format(value, row) : value.toString();
        values.add(formattedValue);
      }
    });
    return Array.from(values).sort();
  };

  // Sorting
  const handleSort = (key) => {
    if (!enableSort) return;
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filtering
  const handleFilterToggle = (columnKey, value) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      if (!newFilters[columnKey]) {
        newFilters[columnKey] = new Set([value]);
      } else {
        const newSet = new Set(newFilters[columnKey]);
        if (newSet.has(value)) {
          newSet.delete(value);
          if (newSet.size === 0) {
            delete newFilters[columnKey];
          } else {
            newFilters[columnKey] = newSet;
          }
        } else {
          newSet.add(value);
          newFilters[columnKey] = newSet;
        }
      }
      return newFilters;
    });
  };

  const clearColumnFilter = (columnKey) => {
    setActiveFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[columnKey];
      return newFilters;
    });
  };

  const clearAllFilters = () => {
    setActiveFilters({});
  };

  // Column visibility
  const handleColumnToggle = (columnKey) => {
    if (lockedColumns.includes(columnKey)) return;
    setVisibleColumns(prev => {
      if (prev.includes(columnKey)) {
        return prev.filter(key => key !== columnKey);
      } else {
        return [...prev, columnKey];
      }
    });
  };

  // Cell editing
  const startEditCell = (rowId, columnKey, currentValue) => {
    if (isLocked || nonEditableColumns.includes(columnKey)) return;
    setEditingCell({ rowId, columnKey });
    setEditValue(currentValue || '');
  };

  // Enhanced saveCell function that supports both callback formats
  const saveCell = async () => {
    if (!editingCell) return;
    
    const { rowId, columnKey } = editingCell;
    const row = data.find(r => r[idField] === rowId);
    const overrideKey = `${rowId}::${columnKey}`;
    const currentValue = overrideKey in cellOverrides ? cellOverrides[overrideKey] : row?.[columnKey];

    if (editValue === (currentValue || '')) {
      setEditingCell(null);
      setEditValue('');
      return;
    }

    setSavingCell({ rowId, columnKey });
    setEditingCell(null);
    setActionError(null);
    // Optimistic: show the edited value immediately; revert on failure.
    setCellOverrides(prev => ({ ...prev, [overrideKey]: editValue }));

    try {
      // Support both callback signatures
      if (isExtendedCallback && updateCallback !== onUpdate) {
        // Extended signature: (rowId, field, value, rowData)
        console.log('EditableDataTable: Using extended callback signature (onCellUpdate/onCellChange)');
        await updateCallback(rowId, columnKey, editValue, row);
      } else {
        // Original signature: (rowId, updates)
        console.log('EditableDataTable: Using original callback signature (onUpdate)');
        await updateCallback(rowId, { [columnKey]: editValue });
      }

      setSavedCell({ rowId, columnKey });
      setTimeout(() => setSavedCell(null), 1500);
    } catch (error) {
      console.error('Error updating cell:', error);
      setCellOverrides(prev => {
        const next = { ...prev };
        delete next[overrideKey];
        return next;
      });
      setActionError('Failed to save. Please try again.');
    } finally {
      setSavingCell(null);
      setEditValue('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      saveCell();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  };

  // Add new row
  const handleAddRow = async () => {
    const requiredFields = columns.filter(c => c.required).map(c => c.key);
    const missingFields = requiredFields.filter(field => !newRowData[field]);
    
    if (missingFields.length > 0) {
      setActionError(`Please fill in required fields: ${missingFields.join(', ')}`);
      return;
    }

    setActionError(null);
    try {
      await onAdd(newRowData);
      setNewRowData({});
      setShowAddRow(false);
    } catch (error) {
      console.error('Error adding row:', error);
      setActionError('Failed to add. Please try again.');
    }
  };

  // Delete row
  const handleDeleteRow = async (rowId) => {
    if (!(await confirmDialog('Are you sure you want to delete this row?'))) return;
    setActionError(null);
    try {
      await onDelete(rowId);
    } catch (error) {
      console.error('Error deleting row:', error);
      setActionError('Failed to delete. Please try again.');
    }
  };

  // Data processing
  const processedData = React.useMemo(() => {
    let result = [...data];
    
    // Apply search
    if (searchTerm && enableSearch) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(row =>
        visibleColumns.some(col => {
          const value = row[col];
          return value?.toString().toLowerCase().includes(searchLower);
        })
      );
    }
    
    // Apply filters
    Object.entries(activeFilters).forEach(([columnKey, selectedValues]) => {
      if (!selectedValues || selectedValues.size === 0) return;
      
      result = result.filter(row => {
        const value = row[columnKey];
        const column = columns.find(c => c.key === columnKey);
        const formattedValue = column?.format ? column.format(value, row) : value?.toString();
        return selectedValues.has(formattedValue);
      });
    });
    
    // Apply sort
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';

        let comparison = 0;
        if (aValue < bValue) comparison = sortConfig.direction === 'asc' ? -1 : 1;
        else if (aValue > bValue) comparison = sortConfig.direction === 'asc' ? 1 : -1;

        // Apply secondary sort fields if primary sort is equal
        if (comparison === 0 && secondarySortFields) {
          for (const field of secondarySortFields) {
            let aSecondary = a[field.key];
            let bSecondary = b[field.key];

            // Handle numeric fields (like line numbers)
            if (field.numeric || field.key.includes('line')) {
              aSecondary = parseInt(aSecondary, 10) || 0;
              bSecondary = parseInt(bSecondary, 10) || 0;
            } else {
              aSecondary = aSecondary || '';
              bSecondary = bSecondary || '';
            }

            if (aSecondary < bSecondary) {
              comparison = field.direction === 'asc' ? -1 : 1;
              break;
            }
            if (aSecondary > bSecondary) {
              comparison = field.direction === 'asc' ? 1 : -1;
              break;
            }
          }
        }

        return comparison;
      });
    }
    
    return result;
  }, [data, searchTerm, activeFilters, sortConfig, visibleColumns, enableSearch, columns, secondarySortFields]);

  // Render helpers
  const renderEditableCell = (row, column) => {
    const rowId = row[idField];
    const columnKey = column.key;
    const overrideKey = `${rowId}::${columnKey}`;
    // Prefer the optimistic value so a saved edit stays visible until the
    // parent's refetched data arrives.
    const value = overrideKey in cellOverrides ? cellOverrides[overrideKey] : row[columnKey];
    const isEditing = editingCell?.rowId === rowId && editingCell?.columnKey === columnKey;
    const isSaving = savingCell?.rowId === rowId && savingCell?.columnKey === columnKey;
    const isSaved = savedCell?.rowId === rowId && savedCell?.columnKey === columnKey;
    const isNonEditable = nonEditableColumns.includes(columnKey);
    const displayValue = column.format ? column.format(value, row) : (value || '');
    
    if (isEditing) {
      const inputType = column.type || 'text';
      
      if (inputType === 'select' && column.options) {
        // Check if options is a function (dynamic) or array (static)
        const optionsList = typeof column.options === 'function' 
          ? column.options(row) // Pass the entire row for context
          : column.options;
          
        return (
          <select
            className="edt-cell-input"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={saveCell}
            onKeyDown={handleKeyPress}
            autoFocus
          >
            <option value="">-- Select --</option>
            {optionsList.map(opt => {
              const optValue = typeof opt === 'object' ? opt.value : opt;
              const optLabel = typeof opt === 'object' ? opt.label : opt;
              return (
                <option key={optValue} value={optValue}>{optLabel}</option>
              );
            })}
          </select>
        );
      }
      
      // Default input for non-select types
      return (
        <input
          className="edt-cell-input"
          type={inputType}
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={saveCell}
          onKeyDown={handleKeyPress}
          autoFocus
        />
      );
    }
    
    if (isSaving) {
      return (
        <div className="edt-cell-saving">
          <span>{displayValue}</span>
          <Loader2 className="edt-saving-indicator animate-spin" size={14} />
        </div>
      );
    }
    
    if (isSaved) {
      return (
        <div className="edt-cell-saved">
          {displayValue}
          <Check className="edt-saved-indicator" size={16} />
        </div>
      );
    }
    
    return (
      <div 
        className={`edt-cell ${isLocked || isNonEditable ? 'non-editable' : 'editable'}`}
        onClick={() => !isLocked && !isNonEditable && startEditCell(rowId, columnKey, value)}
        data-field={columnKey}
      >
        {displayValue || <span className="edt-empty">Click to edit</span>}
      </div>
    );
  };

  const getSortIndicator = (columnKey) => {
    if (!enableSort) return null;
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="edt-sort-indicator" size={14} />;
    }
    return sortConfig.direction === 'asc' ? 
      <ArrowUp className="edt-sort-indicator" size={14} /> : 
      <ArrowDown className="edt-sort-indicator" size={14} />;
  };

  const hasActiveFilters = Object.keys(activeFilters).length > 0;

  const tableContent = (
    <div className={`editable-data-table ${className} ${compactMode ? 'compact' : ''} ${isFullscreen ? 'fullscreen-mode' : ''}`}>
      {/* Header Section */}
      <div className="edt-header">
        {title && <h3 className="edt-title">{title}</h3>}
        <div className="edt-controls">
          {enableLock && (
            <button
              className={`btn btn-sm ${isLocked ? 'btn-danger' : 'btn-success'}`}
              onClick={() => setIsLocked(!isLocked)}
              title={isLocked ? 'Unlock for editing' : 'Lock to prevent edits'}
            >
              {isLocked ? <Lock size={16} /> : <Unlock size={16} />}
              <span>{isLocked ? 'Locked' : 'Unlocked'}</span>
            </button>
          )}
          {enableSearch && (
            <input
              type="text"
              className="input input-sm edt-search"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          )}
          {enableAdd && !isLocked && (
            <button
              className="btn btn-sm btn-primary"
              onClick={() => setShowAddRow(!showAddRow)}
            >
              {showAddRow ? <X size={16} /> : <Plus size={16} />}
              <span>{showAddRow ? 'Cancel' : addButtonText}</span>
            </button>
          )}
          {enableColumnSelector && (
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setShowColumnSelector(!showColumnSelector)}
            >
              <Columns3 size={16} />
              <span>Columns</span>
            </button>
          )}
          {hasActiveFilters && (
            <button
              className="btn btn-sm btn-ghost"
              onClick={clearAllFilters}
            >
              <X size={16} />
              <span>Clear Filters</span>
            </button>
          )}
          {enableExport && (
            <ExportMenu
              data={processedData}
              columns={visibleColumns.map(key => {
                const col = columns.find(c => c.key === key);
                return col ? { key: col.key, label: col.label || col.key } : { key, label: key };
              })}
              filename={title || 'table-export'}
              size="sm"
            />
          )}
          {enableFullscreen && !isFullscreen && (
            <button
              className="btn btn-sm btn-ghost"
              onClick={toggleFullscreen}
              title="Enter fullscreen mode"
            >
              <Maximize2 size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Inline action error — replaces the banned alert() */}
      {actionError && (
        <div className="edt-error-banner" role="alert">
          <span>{actionError}</span>
          <button
            className="edt-error-dismiss"
            onClick={() => setActionError(null)}
            title="Dismiss"
            aria-label="Dismiss error"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Column Selector */}
      {showColumnSelector && (
        <div className="edt-column-selector">
          <h4>Select Visible Columns</h4>
          <div className="edt-column-list">
            {columns.map(column => (
              <label key={column.key} className="edt-column-item">
                <input
                  type="checkbox"
                  checked={visibleColumns.includes(column.key)}
                  onChange={() => handleColumnToggle(column.key)}
                  disabled={lockedColumns.includes(column.key)}
                />
                <span>{column.label}</span>
                {lockedColumns.includes(column.key) && <span className="text-muted text-xs">(locked)</span>}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="edt-table-container" ref={tableRef}>
        <table className="edt-table">
          <thead>
            <tr>
              {visibleColumns.map(columnKey => {
                const column = columns.find(c => c.key === columnKey);
                if (!column) return null;
                const hasFilter = activeFilters[columnKey]?.size > 0;
                const width = columnWidths[columnKey];
                const defaultWidth = column.width;
                const thStyle = width
                  ? { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }
                  : defaultWidth
                    ? { width: defaultWidth, minWidth: defaultWidth, maxWidth: defaultWidth }
                    : {};

                return (
                  <th
                    key={columnKey}
                    className="edt-th"
                    data-source={column.source || ''}
                    style={thStyle}
                  >
                    <div className="edt-th-content">
                      <span 
                        className={`edt-th-label ${enableSort ? 'sortable' : ''}`}
                        onClick={() => handleSort(columnKey)}
                      >
                        {column.label} {getSortIndicator(columnKey)}
                      </span>
                      {enableFilter && (
                        <button
                          className={`edt-filter-button ${hasFilter ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowFilterDropdown(showFilterDropdown === columnKey ? null : columnKey);
                          }}
                        >
                          <ChevronDown size={12} />
                          {hasFilter && <span className="edt-filter-count">({activeFilters[columnKey]?.size})</span>}
                        </button>
                      )}
                    </div>
                    {enableResize && (
                      <div
                        className="edt-resize-handle"
                        onMouseDown={(e) => handleMouseDown(e, columnKey)}
                        ref={el => resizeRefs.current[columnKey] = el}
                      >
                        <GripVertical size={14} />
                      </div>
                    )}
                    {showFilterDropdown === columnKey && (
                      <div 
                        className="edt-filter-dropdown"
                        ref={el => filterDropdownRefs.current[columnKey] = el}
                        onClick={e => e.stopPropagation()}
                      >
                        <div className="edt-filter-header">
                          <span>Filter {column.label}</span>
                          <div className="edt-filter-actions">
                            <button 
                              className="btn-ghost"
                              onClick={() => {
                                const allValues = getUniqueValues(columnKey);
                                setActiveFilters(prev => ({
                                  ...prev,
                                  [columnKey]: new Set(allValues)
                                }));
                              }}
                            >
                              All
                            </button>
                            {hasFilter && (
                              <button 
                                className="btn-ghost"
                                onClick={() => clearColumnFilter(columnKey)}
                              >
                                Clear
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="edt-filter-options">
                          {getUniqueValues(columnKey).map(value => (
                            <label key={value} className="edt-filter-option">
                              <input
                                type="checkbox"
                                checked={activeFilters[columnKey]?.has(value) || false}
                                onChange={() => handleFilterToggle(columnKey, value)}
                              />
                              <span>{value}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </th>
                );
              })}
              {enableDelete && !isLocked && <th className="edt-th edt-actions">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {/* Add new row */}
            {showAddRow && (
              <tr className="edt-new-row">
                {visibleColumns.map(columnKey => {
                  const column = columns.find(c => c.key === columnKey);
                  if (!column) return null;
                  
                  return (
                    <td key={columnKey} data-label={column.label || column.key}>
                      <input
                        className="input input-sm edt-new-input"
                        type={column.type || 'text'}
                        placeholder={column.label + (column.required ? ' *' : '')}
                        value={newRowData[columnKey] || ''}
                        onChange={e => setNewRowData({ ...newRowData, [columnKey]: e.target.value })}
                      />
                    </td>
                  );
                })}
                <td className="edt-actions">
                  <button
                    className="btn btn-sm btn-primary icon-only"
                    onClick={handleAddRow}
                    title="Save"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    className="btn btn-sm btn-secondary icon-only"
                    onClick={() => {
                      setShowAddRow(false);
                      setNewRowData({});
                    }}
                    title="Cancel"
                  >
                    <X size={16} />
                  </button>
                </td>
              </tr>
            )}
            
            {/* Data rows */}
            {processedData.map((row, rowIndex) => (
              <tr key={row[idField]} className={rowClassName ? rowClassName(row, rowIndex) : ''}>
                {visibleColumns.map(columnKey => {
                  const column = columns.find(c => c.key === columnKey);
                  if (!column) return null;
                  const tdWidth = columnWidths[columnKey];
                  const tdDefault = column.width;
                  const tdStyle = tdWidth
                    ? { maxWidth: `${tdWidth}px` }
                    : tdDefault
                      ? { maxWidth: tdDefault }
                      : {};

                  return (
                    <td key={columnKey} data-source={column.source || ''} data-label={column.label || column.key} style={tdStyle}>
                      {renderEditableCell(row, column)}
                    </td>
                  );
                })}
                {enableDelete && !isLocked && (
                  <td className="edt-actions">
                    <button 
                      className="btn btn-sm btn-ghost icon-only text-danger"
                      onClick={() => handleDeleteRow(row[idField])}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        
        {processedData.length === 0 && (
          <div className="edt-empty-state">
            {searchTerm || hasActiveFilters ? 'No results found.' : emptyMessage}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="edt-footer">
        <span className="text-muted">{processedData.length} rows</span>
        <span className="text-muted">{visibleColumns.length} columns</span>
        {hasActiveFilters && <span className="text-muted">{Object.keys(activeFilters).length} filters active</span>}
      </div>
    </div>
  );

  if (isFullscreen) {
    return (
      <div className="edt-fullscreen-overlay">
        <div className="edt-fullscreen-container">
          <button 
            className="edt-fullscreen-close"
            onClick={toggleFullscreen}
            title="Exit fullscreen"
          >
            <X size={20} />
          </button>
          {tableContent}
        </div>
      </div>
    );
  }

  return tableContent;
};

export default EditableDataTable;